import React, { useMemo } from 'react';
import { Box, Chip, Stack, Tooltip } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { sub, startOfMonth, isEqual, startOfDay } from 'date-fns';
import { enGB, enUS, de, es, fr, it, nl } from 'date-fns/locale';

const localeMap = {
  'en-US': enUS,
  'en-GB': enGB,
  'en-AU': enGB,
  'en-CA': enGB,
  'de': de,
  'es': es,
  'fr': fr,
  'it': it,
  'nl': nl,
};

export function getLocale() {
  const lang = navigator.language || 'en-GB';
  return localeMap[lang] || localeMap[lang.split('-')[0]] || enGB;
}

function defaultRange(days) {
  const today = startOfDay(new Date());
  if (days === null) {
    return { start: startOfMonth(today), end: today };
  }
  return { start: sub(today, { days }), end: today };
}

// Default presets (Overview / Partners). Each preset is {label, getRange(), tooltip?}.
export const DEFAULT_PRESETS = [
  { label: 'Last 7 days', getRange: () => defaultRange(7) },
  { label: 'Last 30 days', getRange: () => defaultRange(30) },
  { label: 'Last 90 days', getRange: () => defaultRange(90) },
  { label: 'This month', getRange: () => defaultRange(null) },
];

function getActivePreset(presets, startDate, endDate) {
  for (const preset of presets) {
    const { start, end } = preset.getRange();
    if (isEqual(startOfDay(startDate), startOfDay(start)) && isEqual(startOfDay(endDate), startOfDay(end))) {
      return preset.label;
    }
  }
  return null;
}

/**
 * Date range with preset chips. Backward compatible: existing callers pass only
 * {startDate, endDate, onChange}. The Buyer Insights tabs pass the optional props:
 *  - presets: [{label, getRange, tooltip}] replacing the defaults
 *  - minDate / maxDate: picker limits (default: none / today)
 *  - labels: {start, end} field labels
 *  - startHelperText / endHelperText: notes under the fields
 *  - tooltip: text for an info icon in front of the fields
 *  - chipHeight: preset chip height (touch targets on narrow screens)
 */
const DateRangeSelector = ({
  startDate,
  endDate,
  onChange,
  presets = DEFAULT_PRESETS,
  minDate,
  maxDate,
  labels = { start: 'Start', end: 'End' },
  startHelperText,
  endHelperText,
  tooltip,
  chipHeight,
}) => {
  const locale = useMemo(() => getLocale(), []);
  const activePreset = getActivePreset(presets, startDate, endDate);
  const endMax = maxDate || new Date();
  const chipSx = chipHeight ? { height: chipHeight, '& .MuiChip-label': { px: 1.5 } } : undefined;

  const handlePreset = (preset) => {
    const { start, end } = preset.getRange();
    onChange(start, end);
  };

  const chip = (preset) => (
    <Chip
      key={preset.label}
      label={preset.label}
      onClick={() => handlePreset(preset)}
      color={activePreset === preset.label ? 'primary' : 'default'}
      variant={activePreset === preset.label ? 'filled' : 'outlined'}
      size="small"
      sx={chipSx}
    />
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 2 }, alignItems: { xs: 'stretch', sm: 'flex-start' }, flexWrap: 'wrap' }}>
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, pt: { sm: startHelperText || endHelperText ? 0.75 : 0 } }}>
          {presets.map((preset) =>
            preset.tooltip ? (
              <Tooltip key={preset.label} title={preset.tooltip} arrow enterTouchDelay={0} leaveTouchDelay={4000}>
                {chip(preset)}
              </Tooltip>
            ) : (
              chip(preset)
            )
          )}
        </Stack>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
          {tooltip && (
            <Tooltip title={tooltip} arrow enterTouchDelay={0} leaveTouchDelay={6000}>
              <InfoOutlined sx={{ fontSize: 18, color: 'text.disabled', cursor: 'help', mt: { sm: 1.4 } }} />
            </Tooltip>
          )}
          <DatePicker
            label={labels.start}
            value={startDate}
            onChange={(date) => date && onChange(date, endDate)}
            slotProps={{ textField: { size: 'small', fullWidth: true, helperText: startHelperText, sx: { width: { sm: 250 }, '& .MuiFormHelperText-root': { mx: 0.5, lineHeight: 1.3 } } } }}
            minDate={minDate}
            maxDate={endDate}
          />
          <DatePicker
            label={labels.end}
            value={endDate}
            onChange={(date) => date && onChange(startDate, date)}
            slotProps={{ textField: { size: 'small', fullWidth: true, helperText: endHelperText, sx: { width: { sm: 250 }, '& .MuiFormHelperText-root': { mx: 0.5, lineHeight: 1.3 } } } }}
            minDate={startDate}
            maxDate={endMax}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangeSelector;
